import type { RoleTemplate } from "@sitescene/constant";

import { UserStatus } from "@sitescene/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { meApi } from "@/api/me";
import { queryKeys } from "@/api/query-keys";
import {
	type InviteUserPayload,
	type UpdateUserPayload,
	type UpdateUserPermissionsPayload,
	type UpdateUserStatusPayload,
	type UserResponse,
	usersApi,
} from "@/api/users";
import { IconNotification } from "@/components/notifications/notifications";
import posthog from "@/lib/posthog";
import {
	captureUserInvited,
	captureUserProfileUpdated,
	type UserProfileUpdateSource,
} from "@/utils/user-analytics";

/** Invite API payload plus the role template selected in the invite UI. */
type InviteUserVariables = InviteUserPayload & {
	role?: RoleTemplate;
};

/**
 * Hook for cancelling a pending user invitation
 */
export function useCancelInvite() {
	const queryClient = useQueryClient();

	const {
		error: cancelInviteError,
		isError: isCancelInviteError,
		isPending: isCancellingInvite,
		isSuccess: isCancelInviteSuccess,
		mutate: cancelInvite,
		mutateAsync: cancelInviteAsync,
	} = useMutation({
		mutationFn: async (userId: string) => {
			return await usersApi.cancelInvite(userId);
		},
		onError: (error) => {
			toast.custom((t) => {
				return (
					<IconNotification
						color="error"
						description={error.message}
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="Failed to cancel invitation"
					/>
				);
			});
			console.error("Cancel invite error:", error);
		},
		onSuccess: (_data, userId) => {
			posthog.capture("invite_cancelled", {
				user_id: userId,
			});
			queryClient.removeQueries({
				queryKey: queryKeys.users.detail(userId),
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
			toast.custom((t) => {
				return (
					<IconNotification
						color="success"
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="Invitation cancelled"
					/>
				);
			});
		},
	});

	return {
		cancelInvite,
		cancelInviteAsync,
		cancelInviteError,
		isCancelInviteError,
		isCancelInviteSuccess,
		isCancellingInvite,
	};
}

/**
 * Hook for inviting a new user to the organization
 */
export function useInviteUser() {
	const queryClient = useQueryClient();

	const {
		data: invitedUserData,
		error: invitedUserError,
		isError: isInvitedUserError,
		isPending: isInvitedUserPending,
		isSuccess: isInvitedUserSuccess,
		mutate: inviteUser,
		mutateAsync: inviteUserAsync,
	} = useMutation({
		mutationFn: async ({ role: _role, ...payload }: InviteUserVariables) => {
			return await usersApi.inviteUser(payload);
		},
		onError: (error) => {
			toast.custom((t) => {
				return (
					<IconNotification
						color="error"
						description={error.message}
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="Failed to invite user"
					/>
				);
			});
			console.error("User invite error:", error);
		},
		onSuccess: (invitedUser, variables) => {
			captureUserInvited({
				locations: variables.locations,
				permissions: variables.permissions,
				role: variables.role,
				userId: invitedUser.id,
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
			toast.custom((t) => {
				return (
					<IconNotification
						color="success"
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="User invited successfully"
					/>
				);
			});
		},
	});

	return {
		invitedUserData,
		invitedUserError,
		inviteUser,
		inviteUserAsync,
		isInvitedUserError,
		isInvitedUserPending,
		isInvitedUserSuccess,
	};
}

/**
 * Hook for removing a user from the organization
 */
export function useRemoveUser() {
	const queryClient = useQueryClient();

	const {
		error: removeUserError,
		isError: isRemoveUserError,
		isPending: isRemovingUser,
		isSuccess: isRemoveUserSuccess,
		mutate: removeUser,
		mutateAsync: removeUserAsync,
	} = useMutation({
		mutationFn: async (userId: string) => {
			return await usersApi.removeUser(userId);
		},
		onError: (error) => {
			toast.custom((t) => {
				return (
					<IconNotification
						color="error"
						description={error.message}
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="Failed to remove user from organization"
					/>
				);
			});
			console.error("User removal error:", error);
		},
		onSuccess: (_data, userId) => {
			const previous = queryClient.getQueryData<UserResponse>(
				queryKeys.users.detail(userId),
			);

			posthog.capture("user_removed", {
				previous_status: previous?.status,
				user_id: userId,
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
			toast.custom((t) => {
				return (
					<IconNotification
						color="success"
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="User removed from organization"
					/>
				);
			});
		},
	});

	return {
		isRemoveUserError,
		isRemoveUserSuccess,
		isRemovingUser,
		removeUser,
		removeUserAsync,
		removeUserError,
	};
}

/**
 * Hook for updating the current user's own profile via the ungated
 * `PATCH /me` endpoint. Use this for self-edit flows (activate-account,
 * manage-profile) so users without `user:update` aren't blocked.
 */
export function useUpdateMe() {
	const queryClient = useQueryClient();

	const {
		data: updatedUserData,
		error: updatedUserError,
		isError: isUpdatedUserError,
		isPending: isUpdatedUserPending,
		isSuccess: isUpdatedUserSuccess,
		mutate: updateMe,
		mutateAsync: updateMeAsync,
	} = useMutation({
		mutationFn: async ({
			userData,
		}: {
			errorToastTitle?: string;
			source: UserProfileUpdateSource;
			successToastTitle?: string;
			userData: UpdateUserPayload;
		}) => {
			return await meApi.update(userData);
		},
		onError: (error, variables) => {
			toast.custom((t) => {
				return (
					<IconNotification
						color="error"
						description={error.message}
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title={variables.errorToastTitle ?? "Failed to update profile"}
					/>
				);
			});
			console.error("Me update error:", error);
		},
		onSuccess: (updatedUser, variables) => {
			captureUserProfileUpdated({
				changedFields: Object.keys(variables.userData),
				source: variables.source,
				userId: updatedUser.id,
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.me.all });
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
			const title =
				variables.successToastTitle ?? "Profile updated successfully";
			toast.custom((t) => {
				return (
					<IconNotification
						color="success"
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title={title}
					/>
				);
			});
		},
	});

	return {
		isUpdatedUserError,
		isUpdatedUserPending,
		isUpdatedUserSuccess,
		updatedUserData,
		updatedUserError,
		updateMe,
		updateMeAsync,
	};
}

/**
 * Hook for updating a user's profile (names, phone, notification preferences, auto-notify settings).
 */
export function useUpdateUser() {
	const queryClient = useQueryClient();

	const {
		data: updatedUserData,
		error: updatedUserError,
		isError: isUpdatedUserError,
		isPending: isUpdatedUserPending,
		isSuccess: isUpdatedUserSuccess,
		mutate: updateUser,
		mutateAsync: updateUserAsync,
	} = useMutation({
		mutationFn: async ({
			userData,
			userId,
		}: {
			errorToastTitle?: string;
			source: UserProfileUpdateSource;
			successToastTitle?: string;
			userData: UpdateUserPayload;
			userId: string;
		}) => {
			if (!userId) throw new Error("User ID is required");
			return await usersApi.updateUser(userId, userData);
		},
		onError: (error, variables) => {
			toast.custom((t) => {
				return (
					<IconNotification
						color="error"
						description={error.message}
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title={variables.errorToastTitle ?? "Failed to update user"}
					/>
				);
			});
			console.error("User update error:", error);
		},
		onSuccess: (updatedUser, variables) => {
			captureUserProfileUpdated({
				changedFields: Object.keys(variables.userData),
				source: variables.source,
				userId: updatedUser.id,
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
			queryClient.invalidateQueries({
				queryKey: queryKeys.users.detail(variables.userId),
			});
			const title = variables.successToastTitle ?? "User updated successfully";
			toast.custom((t) => {
				return (
					<IconNotification
						color="success"
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title={title}
					/>
				);
			});
		},
	});

	return {
		isUpdatedUserError,
		isUpdatedUserPending,
		isUpdatedUserSuccess,
		updatedUserData,
		updatedUserError,
		updateUser,
		updateUserAsync,
	};
}

/**
 * Hook for replacing a user's granular permissions and location scope.
 * The backend diffs against the current set and enforces cascade / subset
 * / parity rules.
 */
export function useUpdateUserPermissions() {
	const queryClient = useQueryClient();

	const {
		data: updatedPermissionsData,
		error: updatedPermissionsError,
		isError: isUpdatedPermissionsError,
		isPending: isUpdatedPermissionsPending,
		isSuccess: isUpdatedPermissionsSuccess,
		mutate: updateUserPermissions,
		mutateAsync: updateUserPermissionsAsync,
	} = useMutation({
		mutationFn: async ({
			permissionsData,
			userId,
		}: {
			permissionsData: UpdateUserPermissionsPayload;
			userId: string;
		}) => {
			if (!userId) throw new Error("User ID is required");
			return await usersApi.updateUserPermissions(userId, permissionsData);
		},
		onError: (error) => {
			toast.custom((t) => {
				return (
					<IconNotification
						color="error"
						description={error.message}
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="Failed to update user permissions"
					/>
				);
			});
			console.error("User permissions update error:", error);
		},
		onSuccess: (updatedUser, variables) => {
			const previous = queryClient.getQueryData<UserResponse>(
				queryKeys.users.detail(variables.userId),
			);

			posthog.capture("user_permissions_updated", {
				new_locations: updatedUser.locations,
				new_permissions: updatedUser.permissions,
				previous_locations: previous?.locations,
				previous_permissions: previous?.permissions,
				user_id: variables.userId,
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
			queryClient.invalidateQueries({
				queryKey: queryKeys.users.detail(variables.userId),
			});
			toast.custom((t) => {
				return (
					<IconNotification
						color="success"
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="User saved successfully"
					/>
				);
			});
		},
	});

	return {
		isUpdatedPermissionsError,
		isUpdatedPermissionsPending,
		isUpdatedPermissionsSuccess,
		updatedPermissionsData,
		updatedPermissionsError,
		updateUserPermissions,
		updateUserPermissionsAsync,
	};
}

/**
 * Hook for updating a user's status (activate/deactivate)
 */
export function useUpdateUserStatus() {
	const queryClient = useQueryClient();

	const {
		data: updatedStatusData,
		error: updatedStatusError,
		isError: isUpdatedStatusError,
		isPending: isUpdatedStatusPending,
		isSuccess: isUpdatedStatusSuccess,
		mutate: updateUserStatus,
		mutateAsync: updateUserStatusAsync,
	} = useMutation({
		mutationFn: async ({
			statusData,
			userId,
		}: {
			statusData: UpdateUserStatusPayload;
			userId: string;
		}) => {
			if (!userId) throw new Error("User ID is required");
			return await usersApi.updateUserStatus(userId, statusData);
		},
		onError: (error) => {
			toast.custom((t) => {
				return (
					<IconNotification
						color="error"
						description={error.message}
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title="Failed to update user status"
					/>
				);
			});
			console.error("User status update error:", error);
		},
		onSuccess: (_data, variables) => {
			const previous = queryClient.getQueryData<UserResponse>(
				queryKeys.users.detail(variables.userId),
			);

			posthog.capture("user_status_updated", {
				new_status: variables.statusData.status,
				previous_status: previous?.status,
				user_id: variables.userId,
			});
			queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
			queryClient.invalidateQueries({
				queryKey: queryKeys.users.detail(variables.userId),
			});
			const statusMessage =
				variables.statusData.status === UserStatus.Active
					? "activated"
					: "deactivated";
			toast.custom((t) => {
				return (
					<IconNotification
						color="success"
						hideDismissLabel={true}
						onClose={() => toast.dismiss(t)}
						title={`User ${statusMessage} successfully`}
					/>
				);
			});
		},
	});

	return {
		isUpdatedStatusError,
		isUpdatedStatusPending,
		isUpdatedStatusSuccess,
		updatedStatusData,
		updatedStatusError,
		updateUserStatus,
		updateUserStatusAsync,
	};
}
